import React, { useState, useEffect, useRef, useCallback } from "react"
import { useHistory, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { isDirty } from "redux-form"
import { defineMessages } from "react-intl"

import Box from "@material-ui/core/Box"
import Button from "@material-ui/core/Button"

import { words } from "../../i18n/lstrings"
import userFormSlice from "../../api/userFormSlice"
import FormController from "./formController"
import CustomUserForm, { CustomerUserFormName } from "./CustomUserForm"
import useDirtyMode from "../../hooks/useDirtyMode"
import AlertByLevel from "../../component/AlertByLevel"
import useSmartFormat from "../../i18n/useSmartFormat"
import { actions as dialog } from "../../component/dialog"
import useConfirmDialog from "../../component/dialog/useConfirmDialog"
import uiSlice from "../../commons/uiSlice"
import { actions as notifier } from "../../component/notifier"

const { actions } = userFormSlice
const { actions: ui } = uiSlice

const formMsg = defineMessages({
  submitConfirmation: {
    id: "form.submit.confirmation",
    defaultMessage:
      "Do you really confirm form submission? After submission, any modification won't be possible."
  },
  saveSuccess: {
    id: "form.save.success",
    defaultMessage: "Form successfully saved"
  }
})

function CustomUserFormPage() {
  const { id, initialize } = useParams()
  const history = useHistory()
  const format = useSmartFormat()
  const dispatch = useDispatch()
  const infoMessage = useSelector((state) => state.userForm.infoMessage)
  const initialValues = useSelector((state) => state.userForm.initialValuesUser)
  const { jumpIdentifier } = useSelector((state) => state.auth.user)
  // it does not need form as model here
  const [, toggleRefresh] = useState(false)

  let disableSubmit = false
  if (initialize) {
    disableSubmit = true
  }

  const goBackHandler = useCallback(() => {
    history.goBack()
  }, [history])

  // just a dirty mode when modifying forms
  const { askConfirmation } = useDirtyMode(
    goBackHandler,
    isDirty(CustomerUserFormName)
  )

  // use FormController to handle any state modification
  // it guarantee a good separation of concerns
  const { current: formController } = useRef(
    new FormController(null, toggleRefresh)
  )
  useEffect(() => {
    dispatch(actions.fetchConfigDocusign())
  }, [dispatch])

  useEffect(() => {
    dispatch(actions.fetchFormById(id)).then((data) => {
      formController.updateForm(data)
    })
  }, [dispatch, formController, id, jumpIdentifier])

  /**
   * Real submit implementation.
   * @param formValues
   * @returns {*}
   */
  const submit = (formValues) => {
    dispatch(ui.toggleLinearProgress())
    const nextStep = formController.isPersistent()
      ? actions.save // A persistent form never goes into 'submitted' status
      : actions.submit
    const isSignable = formController?.form?.signable
    const redirectTo = isSignable
      ? () => history.push(`/esign/${formController?.form?._id}`)
      : () => history.push("/documents")
    return dispatch(nextStep(id, formValues))
      .then((response) => {
        if (response !== undefined && response.status === 200) {
          redirectTo()
        }
        // else do nothing, error has been process by global redux catcher
      })
      .finally(() => dispatch(ui.toggleLinearProgress()))
  }

  // configure dialog connection which is triggering the submission
  useConfirmDialog("submitFormConfirmation", (arg) => submit(arg.formValues))

  // intermediate onSubmit handler to show a dialog confirmation
  // keeping formValues
  const onSubmitShowConfirmDialog = (formValues) => {
    dispatch(
      dialog.show(formMsg.submitConfirmation, {
        toConfirm: { submitFormConfirmation: true, formValues }
      })
    )
  }

  const goBack = () => askConfirmation()

  const save = async (formValues) => {
    await dispatch(actions.save(id, formValues))
    await dispatch(notifier.enqueueSuccess(formMsg.saveSuccess))
  }

  if (!formController.form && !infoMessage) {
    return null
  }

  if (infoMessage) {
    const messages = [{ info: format(infoMessage) }]
    return (
      <Box mt={5}>
        <AlertByLevel message={messages} />
        <Box display="flex" justifyContent="flex-end">
          <Button variant="contained" color="primary" onClick={goBackHandler}>
            {format(words.return)}
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <CustomUserForm
      controller={formController}
      onSubmit={onSubmitShowConfirmDialog}
      save={save}
      goBack={goBack}
      initialValues={initialValues}
      disableSubmit={disableSubmit}
    />
  )
}

export default CustomUserFormPage
